import { computed, getCurrentInstance, unref, type MaybeRef } from 'vue'
import { IRequestPayload, IResponse, IValidationError } from '@/types'
import { Permissions, User } from '@/classes/generated-models'
import { IBaseModuleState } from '@/store/interfaces/IBase.module'
import { AppStore, AppStoreModuleName } from '@/store/types'
import { hasAnyPermissionCheck, hasPermissionCheck } from '@/utils/permission'
import storeSingleton from '@/store'

type AppProxy = {
  $store?: AppStore
  $t?: (key: string, args?: unknown[]) => string
}

function createEmptyState(): IBaseModuleState<any, any> {
  return {
    data: undefined,
    service: undefined as any,
    loading: false,
    validationErrors: [],
    responseException: { exceptionMessage: '', validationErrors: [] },
  }
}

export function useRobaComponent(storeName: MaybeRef<AppStoreModuleName>) {
  const instance = getCurrentInstance()
  const proxy = (instance?.proxy || {}) as AppProxy

  const store = computed<AppStore>(() => {
    return (proxy.$store as AppStore | undefined) || (storeSingleton as AppStore)
  })

  const currentState = computed<IBaseModuleState<any, any>>(() => {
    const moduleState = store.value?.state?.[unref(storeName)] as IBaseModuleState<any, any> | undefined
    return moduleState || createEmptyState()
  })

  const storeData = computed(() => currentState.value.data ?? undefined)
  const isLoading = computed(() => currentState.value.loading)
  const currentUser = computed(() => store.value.state.Account.currentUser as User)
  const impersonated = computed(() => store.value.state.Account.impersonated)
  const impersonatedWithOriginalRights = computed(() => store.value.state.Account.impersonatedWithOriginalRights)
  const multiCompanies = computed(() => {
    const user = currentUser.value
    return user.companies!.length > 1 && !user.combineCompanies
  })

  function translate(key: string, args?: unknown[]): string {
    if (typeof proxy.$t === 'function') {
      return String(proxy.$t(key, args) || key)
    }

    return key
  }

  function errorMessage(field: string): string | undefined {
    if (currentState.value.validationErrors) {
      const message = currentState.value.validationErrors.find((error: IValidationError) => error.name === field
        || error.name.toLowerCase().includes(field.toLowerCase()))
      return message ? translate(`VALIDATION_ERROR.${message.reason.toUpperCase()}`) : undefined
    }
  }

  function errorMessages(fields: string[]): string[] {
    const messages: string[] = []
    fields.forEach((field) => {
      const message = errorMessage(field)
      if (message) messages.push(message)
    })
    return messages
  }

  async function update<TEntity>(payload: IRequestPayload<TEntity>): Promise<IResponse<TEntity>> {
    return await store.value.dispatch(`${unref(storeName)}/update`, payload)
  }

  async function add<TEntity>(payload: IRequestPayload<TEntity>): Promise<IResponse<TEntity>> {
    return await store.value.dispatch(`${unref(storeName)}/add`, payload)
  }

  async function remove(id: number): Promise<IResponse<boolean>> {
    return await store.value.dispatch(`${unref(storeName)}/delete`, id)
  }

  async function fetchData(): Promise<void> {
    await store.value.dispatch(`${unref(storeName)}/fetchData`)
  }

  async function fetchDataFiltered(filter: string): Promise<void> {
    await store.value.dispatch(`${unref(storeName)}/fetchDataFiltered`, filter)
  }

  function hasPermission(permission: Permissions): boolean {
    return hasPermissionCheck(currentUser.value, permission)
  }

  function hasAnyPermission(permissionsToCheck: Permissions[]): boolean {
    return hasAnyPermissionCheck(currentUser.value, permissionsToCheck)
  }

  function defaultStoreExceptionLog(): void {
    console.warn(`Something goes wrong in the ${unref(storeName)} store`)
  }

  return {
    add,
    currentState,
    currentUser,
    defaultStoreExceptionLog,
    errorMessage,
    errorMessages,
    fetchData,
    fetchDataFiltered,
    hasAnyPermission,
    hasPermission,
    impersonated,
    impersonatedWithOriginalRights,
    isLoading,
    multiCompanies,
    permissions: Permissions,
    remove,
    store,
    storeData,
    update,
  }
}
